/**
 * @file Expandable bidder lot table with scan-status merge.
 */

import React, { useMemo } from 'react';
import { Loader, CheckCircle, AlertCircle } from 'lucide-react';
import { useExpandedBidderLots } from '../hooks/useExpandedBidderLots';
import { sortLotsByAuctionThenLot } from '../utils/sortLots';

function formatInvoiceStatus(status) {
  if (!status) return 'N/A';
  const index = status.indexOf(' - ');
  return index !== -1 ? status.substring(0, index) : status;
}

function BidderExpandedLots({ bidderId, auctionGroupId, enabled }) {
  const { lots, lotsWithScan, isLoading, error, refetch } = useExpandedBidderLots(
    bidderId,
    auctionGroupId,
    enabled
  );

  const scanByLotKey = useMemo(() => {
    const map = new Map();
    if (!lotsWithScan) return map;
    for (const l of lotsWithScan) {
      map.set(`${l.lot_number}\0${l.auction_id ?? ''}`, l);
      if (!map.has(String(l.lot_number))) {
        map.set(String(l.lot_number), l);
      }
    }
    return map;
  }, [lotsWithScan]);

  if (isLoading) {
    return (
      <div className="loading">
        <Loader className="spin" size={20} />
        Loading lots...
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <strong>Error loading lots:</strong> {error.message}
        <button type="button" className="btn btn-secondary" style={{ marginLeft: '12px' }} onClick={() => refetch()}>
          Retry
        </button>
      </div>
    );
  }

  if (!lots?.length) {
    return <p>No lots found for this bidder.</p>;
  }

  return (
    <div>
      <h4 style={{ marginBottom: '10px' }}>Lot Details:</h4>
      <table className="table" style={{ marginTop: '10px' }}>
        <thead>
          <tr>
            <th>Lot #</th>
            <th>Location</th>
            <th>Title</th>
            <th>Auction ID</th>
            <th>Current Bid</th>
            <th>Invoice Status</th>
            <th>Scan Status</th>
          </tr>
        </thead>
        <tbody>
          {sortLotsByAuctionThenLot(lots).map((lot, idx) => {
            const scanData =
              scanByLotKey.get(`${lot.lot_number}\0${lot.auction_id ?? ''}`) ||
              scanByLotKey.get(String(lot.lot_number));
            const isScanned = scanData?.is_scanned || false;
            return (
              <tr
                key={`${bidderId}-${lot.lot_number}-${lot.auction_id || idx}`}
                style={{ backgroundColor: isScanned ? '#d4edda' : 'transparent' }}
              >
                <td>{lot.lot_number}</td>
                <td>{lot.location_tag || scanData?.location_tag || '—'}</td>
                <td>{lot.lot_title}</td>
                <td>{lot.auction_id || 'N/A'}</td>
                <td>${parseFloat(lot.current_bid || 0).toFixed(2)}</td>
                <td>{formatInvoiceStatus(lot.invoice_status)}</td>
                <td>
                  {isScanned ? (
                    <span style={{ color: '#28a745', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <CheckCircle size={16} />
                      Scanned
                    </span>
                  ) : (
                    <span style={{ color: '#ffc107', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <AlertCircle size={16} />
                      Not Scanned
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default BidderExpandedLots;
